import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
};

const panelVariants = {
  hidden: { opacity: 0, scale: 0.92, y: 40 },
  visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.45, ease: 'easeOut' } },
  exit: { opacity: 0, scale: 0.92, y: 40, transition: { duration: 0.3, ease: 'easeIn' } },
};

const lineVariant = {
  hidden: { scaleX: 0 },
  visible: { scaleX: 1, transition: { duration: 0.6, delay: 0.3, ease: 'easeOut' } },
};

const infoVariants = {
  hidden: { opacity: 0, x: 30 },
  visible: (i) => ({
    opacity: 1,
    x: 0,
    transition: { delay: 0.25 + i * 0.1, duration: 0.5, ease: 'easeOut' },
  }),
};

export default function MovieDetailModal({ movie, onClose }) {
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  if (!movie) return null;

  const isComingSoon = movie.rating === 'Coming Soon';

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[9999] flex items-center justify-center px-4 py-8"
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="hidden"
        style={{ background: 'rgba(5,5,5,0.88)', backdropFilter: 'blur(12px)' }}
        onClick={onClose}
      >
        <motion.div
          className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-sm flex flex-col md:flex-row"
          variants={panelVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1410 50%, #0f0f0f 100%)',
            border: '1px solid rgba(212,175,55,0.25)',
            boxShadow: '0 0 80px rgba(243,112,33,0.15), 0 0 160px rgba(212,175,55,0.08)',
          }}
        >
          {/* Gold corner accents */}
          <div className="absolute top-0 left-0 w-12 h-12 border-t-2 border-l-2 border-brand-gold/60 z-10 pointer-events-none" />
          <div className="absolute bottom-0 right-0 w-12 h-12 border-b-2 border-r-2 border-brand-gold/60 z-10 pointer-events-none" />
          <div className="absolute top-0 right-0 w-12 h-12 border-t-2 border-r-2 border-brand-orange/40 z-10 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-12 h-12 border-b-2 border-l-2 border-brand-orange/40 z-10 pointer-events-none" />

          {/* LEFT — Poster */}
          <div className="relative w-full md:w-[42%] flex-shrink-0 overflow-hidden" style={{ minHeight: '220px', maxHeight: '320px' }}>
            <motion.img
              src={movie.poster}
              alt={movie.title}
              className="w-full h-full object-cover"
              initial={{ scale: 1.08, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.7, ease: 'easeOut' }}
              style={{ filter: 'brightness(0.88)' }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-transparent to-[#0f0f0f] hidden md:block" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0f0f0f] via-transparent to-transparent md:hidden" />

            <div className="absolute top-4 left-4">
              {isComingSoon ? (
                <span className="font-cinzel text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-sm text-brand-gold"
                  style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.4)' }}>
                  Coming Soon
                </span>
              ) : (
                <span className="font-cinzel text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-sm text-white"
                  style={{ background: 'rgba(243,112,33,0.85)', border: '1px solid rgba(243,112,33,0.5)' }}>
                  Released
                </span>
              )}
            </div>
          </div>

          {/* RIGHT — Info */}
          <div className="flex-1 flex flex-col justify-center px-5 sm:px-8 py-8 md:py-12">
            <motion.div
              custom={0} variants={infoVariants} initial="hidden" animate="visible"
              className="flex items-center gap-3 mb-5"
            >
              <div className="h-px w-8 bg-brand-orange" />
              <span className="font-cinzel text-[10px] tracking-[0.25em] uppercase text-brand-orange">
                Magic Moon Studios
              </span>
            </motion.div>

            <motion.h2
              custom={1} variants={infoVariants} initial="hidden" animate="visible"
              className="font-cinzel font-black text-3xl md:text-4xl lg:text-5xl leading-tight mb-2"
              style={{
                background: 'linear-gradient(135deg, #fff 0%, #D4AF37 60%, #f37021 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {movie.title}
            </motion.h2>

            <motion.div
              className="h-px bg-gradient-to-r from-brand-gold via-brand-orange to-transparent mb-6 origin-left"
              variants={lineVariant} initial="hidden" animate="visible"
            />

            <motion.div
              custom={2} variants={infoVariants} initial="hidden" animate="visible"
              className="flex flex-wrap gap-3 mb-7"
            >
              <Chip label={movie.genre} color="orange" />
              <Chip label={movie.year} color="gold" />
              {!isComingSoon && <Chip label={`IMDb ${movie.rating}`} color="green" />}
            </motion.div>

            <motion.p
              custom={3} variants={infoVariants} initial="hidden" animate="visible"
              className="font-inter text-white/65 text-sm leading-relaxed mb-8 max-w-md"
            >
              {movie.desc}
            </motion.p>

            <motion.div
              custom={4} variants={infoVariants} initial="hidden" animate="visible"
              className="flex items-center gap-4 mb-8"
            >
              <div className="h-px flex-1 bg-brand-gold/15" />
              <span className="font-cinzel text-[9px] tracking-widest uppercase text-brand-gold/40">
                Production Details
              </span>
              <div className="h-px flex-1 bg-brand-gold/15" />
            </motion.div>

            <motion.div
              custom={5} variants={infoVariants} initial="hidden" animate="visible"
              className="grid grid-cols-2 gap-x-6 gap-y-4 mb-10"
            >
              <DetailRow label="Studio" value="Magic Moon Studios" />
              <DetailRow label="Genre" value={movie.genre} />
              <DetailRow label="Year" value={movie.year} />
              <DetailRow label="Rating" value={isComingSoon ? 'Coming Soon' : `IMDb ${movie.rating}`} />
              <DetailRow label="Language" value="Kannada" />
              <DetailRow label="Status" value={isComingSoon ? 'In Production' : 'Released'} />
            </motion.div>

            <motion.div
              custom={6} variants={infoVariants} initial="hidden" animate="visible"
              className="flex gap-3 flex-wrap"
            >
              {!isComingSoon && movie.trailerUrl && (
                <a
                  href={movie.trailerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-cinzel text-xs tracking-widest uppercase px-5 sm:px-6 py-3 rounded-sm transition-all duration-300"
                  style={{ background: 'linear-gradient(135deg,#f37021,#D4AF37)', color: '#0f0f0f' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Watch Trailer
                </a>
              )}
              <button
                onClick={onClose}
                className="font-cinzel text-xs tracking-widest uppercase px-5 sm:px-6 py-3 rounded-sm transition-all duration-300 text-white/60 hover:text-brand-gold"
                style={{ border: '1px solid rgba(212,175,55,0.3)' }}
              >
                Close
              </button>
            </motion.div>
          </div>

          {/* X Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-8 h-8 flex items-center justify-center rounded-sm text-white/50 hover:text-brand-gold transition-all duration-200"
            style={{ border: '1px solid rgba(212,175,55,0.2)', background: 'rgba(15,15,15,0.7)' }}
            aria-label="Close"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function Chip({ label, color }) {
  const styles = {
    orange: { background: 'rgba(243,112,33,0.12)', border: '1px solid rgba(243,112,33,0.4)', color: '#f37021' },
    gold:   { background: 'rgba(212,175,55,0.12)',  border: '1px solid rgba(212,175,55,0.4)',  color: '#D4AF37' },
    green:  { background: 'rgba(100,200,120,0.1)',  border: '1px solid rgba(100,200,120,0.3)', color: '#7ecb8f' },
  };
  return (
    <span className="font-cinzel text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-sm" style={styles[color]}>
      {label}
    </span>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="font-cinzel text-[9px] tracking-[0.2em] uppercase text-brand-gold/50 mb-1">{label}</p>
      <p className="font-inter text-white/80 text-sm">{value}</p>
    </div>
  );
}
